import {
  EyeIcon,
  SignalIcon,
  CodeBracketIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  LifebuoyIcon,
  KeyIcon,
  BoltIcon,
  UserGroupIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/solid';

/**
 * User-facing metadata for every assignable IoT Portal role.
 *
 * The source of truth for what each role can actually do is the authoritative
 * permission list in `alm-cdk/auth-lambda/config/roles.json`. The descriptions
 * here are friendly summaries of those permissions for END USERS and must NOT be
 * treated as a security boundary — the backend always enforces authorization.
 *
 * Each entry provides:
 *  - name:        machine role name (matches Cognito group / roles.json)
 *  - label:       human-friendly display name
 *  - description: 1–2 sentence user-facing explanation
 *  - category:    grouping bucket for the legend
 *  - color:       Tailwind class set used to render colored badges/cards
 *  - Icon:        Heroicon component representing the role
 */
export const ROLE_METADATA = {
  viewer: {
    name: 'viewer',
    label: 'Viewer',
    category: 'Read-only',
    description:
      'Read-only access to a project: can view assets, deployments, telemetry, machine events, KPIs, reports and device status, but cannot make any changes.',
    Icon: EyeIcon,
    color: {
      badge:
        'bg-gradient-to-r from-slate-50 to-slate-100 text-slate-700 border border-slate-300 ring-1 ring-slate-200/60 shadow-sm',
      dot: 'bg-slate-400',
      iconText: 'text-slate-600',
      iconBg: 'bg-slate-100',
      ring: 'ring-slate-200',
      avatar: 'from-slate-400 to-slate-500',
    },
  },
  telemetry: {
    name: 'telemetry',
    label: 'Telemetry',
    category: 'Read-only',
    description:
      'Focused on data and telemetry: can read machine events, brand consumptions, telemetry, lifecycle and sandbox data, IoT reports and device connectivity. No asset or deployment management.',
    Icon: SignalIcon,
    color: {
      badge:
        'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 border border-gray-300 ring-1 ring-gray-200/60 shadow-sm',
      dot: 'bg-gray-400',
      iconText: 'text-gray-600',
      iconBg: 'bg-gray-100',
      ring: 'ring-gray-200',
      avatar: 'from-gray-400 to-gray-500',
    },
  },
  developer: {
    name: 'developer',
    label: 'Developer',
    category: 'Operations',
    description:
      'For development work: can manage assets and create or delete deployments in test environments, and use the Data Dictionary. Intended for non-production iteration.',
    Icon: CodeBracketIcon,
    color: {
      badge:
        'bg-gradient-to-r from-sky-50 to-cyan-100 text-sky-800 border border-sky-300 ring-1 ring-sky-200/60 shadow-sm',
      dot: 'bg-sky-500',
      iconText: 'text-sky-600',
      iconBg: 'bg-sky-100',
      ring: 'ring-sky-200',
      avatar: 'from-sky-500 to-cyan-500',
    },
  },
  releaseManager: {
    name: 'releaseManager',
    label: 'Release Manager',
    category: 'Operations',
    description:
      'Manages the full release lifecycle: assets, bundles, deployments and waves, device onboarding, remote functions (SRF), Data Dictionary import/export and Device Defender (block/release devices). Cannot create global production deployments.',
    Icon: RocketLaunchIcon,
    color: {
      badge:
        'bg-gradient-to-r from-blue-50 to-indigo-100 text-blue-800 border border-blue-300 ring-1 ring-blue-200/60 shadow-sm',
      dot: 'bg-blue-500',
      iconText: 'text-blue-600',
      iconBg: 'bg-blue-100',
      ring: 'ring-blue-200',
      avatar: 'from-blue-500 to-indigo-500',
    },
  },
  superadmin: {
    name: 'superadmin',
    label: 'Super Admin',
    category: 'Administration',
    description:
      'Project administrator: everything a Release Manager can do plus global deployments, credentials, user analytics and audit logs. Does not include user management (create/delete/assign roles), which is granted by the dedicated User Management role.',
    Icon: ShieldCheckIcon,
    color: {
      badge:
        'bg-gradient-to-r from-purple-50 to-fuchsia-100 text-purple-800 border border-purple-300 ring-1 ring-purple-200/60 shadow-sm',
      dot: 'bg-purple-500',
      iconText: 'text-purple-600',
      iconBg: 'bg-purple-100',
      ring: 'ring-purple-200',
      avatar: 'from-purple-500 to-fuchsia-500',
    },
  },
  userManagement: {
    name: 'userManagement',
    label: 'User Management',
    category: 'Administration',
    description:
      'Dedicated user administrator: can create, delete and assign/remove roles for users — but only within the projects they administer. Cannot grant rootAccess. This is the only role (besides Root Access) that can manage users.',
    Icon: UserGroupIcon,
    color: {
      badge:
        'bg-gradient-to-r from-indigo-50 to-violet-100 text-indigo-800 border border-indigo-300 ring-1 ring-indigo-200/60 shadow-sm',
      dot: 'bg-indigo-500',
      iconText: 'text-indigo-600',
      iconBg: 'bg-indigo-100',
      ring: 'ring-indigo-200',
      avatar: 'from-indigo-500 to-violet-500',
    },
  },
  l3Support: {
    name: 'l3Support',
    label: 'L3 Support',
    category: 'Support',
    description:
      'Level-3 support: elevated cross-project troubleshooting access to investigate and resolve incidents. Typically granted to the platform support team.',
    Icon: LifebuoyIcon,
    color: {
      badge:
        'bg-gradient-to-r from-amber-50 to-orange-100 text-amber-800 border border-amber-300 ring-1 ring-amber-200/60 shadow-sm',
      dot: 'bg-amber-500',
      iconText: 'text-amber-600',
      iconBg: 'bg-amber-100',
      ring: 'ring-amber-200',
      avatar: 'from-amber-500 to-orange-500',
    },
  },
  certificateadmin: {
    name: 'certificateadmin',
    label: 'Certificate Admin',
    category: 'Security',
    description:
      "Manages device security certificates: provisioning, status and quota for a project's fleet.",
    Icon: KeyIcon,
    color: {
      badge:
        'bg-gradient-to-r from-emerald-50 to-teal-100 text-emerald-800 border border-emerald-300 ring-1 ring-emerald-200/60 shadow-sm',
      dot: 'bg-emerald-500',
      iconText: 'text-emerald-600',
      iconBg: 'bg-emerald-100',
      ring: 'ring-emerald-200',
      avatar: 'from-emerald-500 to-teal-500',
    },
  },
  rootAccess: {
    name: 'rootAccess',
    label: 'Root Access',
    category: 'Administration',
    description:
      'Global super-administrator with unrestricted access to every project and every capability, including user and platform administration. Assignable only by other root administrators.',
    Icon: BoltIcon,
    color: {
      badge:
        'bg-gradient-to-r from-rose-500 via-red-500 to-orange-500 text-white border border-rose-600 shadow ring-1 ring-rose-300/50',
      dot: 'bg-white',
      iconText: 'text-rose-600',
      iconBg: 'bg-rose-100',
      ring: 'ring-rose-300',
      avatar: 'from-rose-500 to-red-600',
    },
  },
};

/**
 * Fallback metadata used when a group string references an unknown role.
 * @param {string} name - The unknown role name.
 * @returns {object} A neutral metadata entry.
 */
export function getUnknownRoleMetadata(name) {
  return {
    name,
    label: name,
    category: 'Other',
    description: 'Custom or legacy role. No detailed description is available.',
    Icon: QuestionMarkCircleIcon,
    color: {
      badge:
        'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-600 border border-gray-300 ring-1 ring-gray-200/60 shadow-sm',
      dot: 'bg-gray-400',
      iconText: 'text-gray-500',
      iconBg: 'bg-gray-100',
      ring: 'ring-gray-200',
      avatar: 'from-gray-400 to-gray-500',
    },
  };
}

/**
 * Resolve metadata for a given role name, falling back gracefully.
 * @param {string} roleName - The machine role name (e.g. `developer`).
 * @returns {object} Role metadata.
 */
export function getRoleMetadata(roleName) {
  return ROLE_METADATA[roleName] || getUnknownRoleMetadata(roleName);
}

/**
 * Display order and color accent for each category in the legend.
 */
export const CATEGORY_ORDER = [
  'Administration',
  'Operations',
  'Support',
  'Security',
  'Read-only',
];

export const CATEGORY_ACCENT = {
  Administration: 'text-purple-700',
  Operations: 'text-blue-700',
  Support: 'text-amber-700',
  Security: 'text-emerald-700',
  'Read-only': 'text-slate-600',
  Other: 'text-gray-600',
};

/**
 * Gradient accent (Tailwind `from-*`/`to-*`) used for colored category headers
 * and chips in the roles legend.
 */
export const CATEGORY_BG = {
  Administration: 'from-purple-500 to-fuchsia-500',
  Operations: 'from-blue-500 to-sky-500',
  Support: 'from-amber-500 to-orange-500',
  Security: 'from-emerald-500 to-teal-500',
  'Read-only': 'from-slate-400 to-slate-500',
  Other: 'from-gray-400 to-gray-500',
};

/**
 * Parse a Cognito group string into its project + role parts.
 *
 * Groups are stored as either `rootAccess` (global) or `<project>:<role>`.
 * @param {string} group - The raw group string.
 * @returns {{project: string|null, role: string, raw: string}} Parsed parts.
 */
export function parseGroup(group) {
  const raw = (group || '').trim();
  if (raw === 'rootAccess') {
    return { project: null, role: 'rootAccess', raw };
  }
  const parts = raw.split(':');
  if (parts.length === 2 && parts[0] && parts[1]) {
    return { project: parts[0], role: parts[1], raw };
  }
  return { project: null, role: raw, raw };
}

/**
 * All roles grouped by category, in display order, for legend rendering.
 * @returns {Array<{category: string, roles: Array<object>}>}
 */
export function getRolesByCategory() {
  const grouped = CATEGORY_ORDER.map((category) => ({
    category,
    roles: Object.values(ROLE_METADATA).filter((r) => r.category === category),
  }));
  return grouped.filter((g) => g.roles.length > 0);
}
